/**
 * 🔊 SPACE SOUNDS - Système de sons immersifs
 * Sons de vaisseau spatial pour l'interface admin
 */

// URLs des sons (base64 ou CDN)
// On utilise des sons générés en base64 pour éviter les dépendances externes
const SOUNDS = {
  // Bip de navigation (court)
  nav: 'data:audio/wav;base64,UklGRhwAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YfgAAAB/f39/f39/f39/',
  
  // Son de validation (success)
  success: null,
  
  // Son d'erreur
  error: null,
  
  // Son de notification
  notification: null,
  
  // Son de hover
  hover: null,
  
  // Son d'hologramme (activation)
  hologram: null,
  
  // Son de transmission (message)
  transmission: null,
};

// Créer un son synthétique avec Web Audio API
const createSound = (type) => {
  if (typeof window === 'undefined' || !window.AudioContext) return null;
  
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  
  const createOscillator = (frequency, duration, type = 'sine', gain = 0.3) => {
    return () => {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = type;
      gainNode.gain.setValueAtTime(gain, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + duration);
    };
  };
  
  const createBeep = (frequencies, durations, waveType = 'sine') => {
    return () => {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      let currentTime = ctx.currentTime;
      
      frequencies.forEach((freq, i) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        
        oscillator.frequency.value = freq;
        oscillator.type = waveType;
        gainNode.gain.setValueAtTime(0.2, currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, currentTime + durations[i]);
        
        oscillator.start(currentTime);
        oscillator.stop(currentTime + durations[i]);
        
        currentTime += durations[i] * 0.8;
      });
    };
  };
  
  switch (type) {
    case 'nav':
      // Bip court de navigation
      return createOscillator(880, 0.08, 'sine', 0.15);
    
    case 'hover':
      // Son subtil de hover
      return createOscillator(1200, 0.05, 'sine', 0.08);
    
    case 'success':
      // Mélodie de succès (2 notes ascendantes)
      return createBeep([523.25, 783.99], [0.12, 0.2], 'sine');
    
    case 'error':
      // Son d'erreur (2 notes descendantes)
      return createBeep([440, 330], [0.15, 0.25], 'sawtooth');
    
    case 'notification':
      // Son de notification (3 notes)
      return createBeep([659.25, 783.99, 987.77], [0.1, 0.1, 0.15], 'sine');
    
    case 'hologram':
      // Son d'activation hologramme (sweep)
      return () => {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(200, ctx.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(1000, ctx.currentTime + 0.2);
        
        gainNode.gain.setValueAtTime(0.15, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
        
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.3);
      };
    
    case 'transmission':
      // Son de transmission/message
      return createBeep([440, 880, 440], [0.1, 0.1, 0.1], 'square');
    
    case 'warp':
      // Son de warp/hypervitesse
      return () => {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        const filter = ctx.createBiquadFilter();
        
        oscillator.connect(filter);
        filter.connect(gainNode);
        gainNode.connect(ctx.destination);
        
        oscillator.type = 'sawtooth';
        oscillator.frequency.setValueAtTime(100, ctx.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(2000, ctx.currentTime + 0.5);
        
        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(500, ctx.currentTime);
        filter.frequency.exponentialRampToValueAtTime(5000, ctx.currentTime + 0.5);
        
        gainNode.gain.setValueAtTime(0.2, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.6);
        
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.6);
      };
    
    case 'alert':
      // Son d'alerte
      return () => {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        let time = ctx.currentTime;
        
        for (let i = 0; i < 3; i++) {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          
          osc.connect(gain);
          gain.connect(ctx.destination);
          
          osc.type = 'square';
          osc.frequency.value = 800;
          
          gain.gain.setValueAtTime(0.15, time);
          gain.gain.setValueAtTime(0, time + 0.1);
          
          osc.start(time);
          osc.stop(time + 0.1);
          
          time += 0.2;
        }
      };
    
    default:
      return createOscillator(440, 0.1);
  }
};

// Classe principale du système de sons
class SpaceSoundSystem {
  constructor() {
    this.enabled = true;
    this.volume = 0.5;
    this.sounds = {};
    this.initialized = false;
  }
  
  init() {
    if (this.initialized) return;
    
    // Créer les sons
    this.sounds = {
      nav: createSound('nav'),
      hover: createSound('hover'),
      success: createSound('success'),
      error: createSound('error'),
      notification: createSound('notification'),
      hologram: createSound('hologram'),
      transmission: createSound('transmission'),
      warp: createSound('warp'),
      alert: createSound('alert'),
    };
    
    // Charger les préférences
    const savedEnabled = localStorage.getItem('spaceSounds');
    if (savedEnabled !== null) {
      this.enabled = savedEnabled === 'true';
    }
    
    const savedVolume = localStorage.getItem('spaceSoundsVolume');
    if (savedVolume !== null) {
      this.volume = parseFloat(savedVolume);
    }
    
    this.initialized = true;
  }
  
  play(soundName) {
    if (!this.enabled || !this.initialized) return;
    
    const sound = this.sounds[soundName];
    if (sound && typeof sound === 'function') {
      try {
        sound();
      } catch (e) {
        console.warn('Sound play error:', e);
      }
    }
  }
  
  setEnabled(enabled) {
    this.enabled = enabled;
    localStorage.setItem('spaceSounds', enabled.toString());
  }
  
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
    localStorage.setItem('spaceSoundsVolume', this.volume.toString());
  }
  
  toggle() {
    this.setEnabled(!this.enabled);
    return this.enabled;
  }
}

// Instance singleton
const spaceSounds = new SpaceSoundSystem();

// Hook React pour utiliser les sons
export const useSpaceSounds = () => {
  const [enabled, setEnabled] = React.useState(spaceSounds.enabled);
  
  React.useEffect(() => {
    spaceSounds.init();
    setEnabled(spaceSounds.enabled);
  }, []);
  
  const playSound = React.useCallback((soundName) => {
    spaceSounds.play(soundName);
  }, []);
  
  const toggleSounds = React.useCallback(() => {
    const newState = spaceSounds.toggle();
    setEnabled(newState);
    return newState;
  }, []);
  
  return {
    enabled,
    playSound,
    toggleSounds,
    setEnabled: (val) => {
      spaceSounds.setEnabled(val);
      setEnabled(val);
    },
  };
};

// Import React pour le hook
import React from 'react';

export default spaceSounds;
